/** Widths share the existing personalization envelope/table key, never a second store. */
export type TableColumnWidthPreferences = Record<
  string,
  Record<string, number>
>;

// A bounded override is deliberate: a typo must not create a kilometre-wide
// compliance table. Column-specific minimums remain authoritative at render time.
export const TABLE_COLUMN_WIDTH_MAX_PX = 1200;
export const TABLE_COLUMN_WIDTH_STEP_PX = 10;

export function normalizeTableColumnWidths(
  value: unknown
): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Record<string, number> = {};
  for (const [key, width] of Object.entries(value)) {
    if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
      result[key] = Math.min(TABLE_COLUMN_WIDTH_MAX_PX, width);
    }
  }
  return result;
}

export function getTableColumnWidthPreferencesFromPersistedStorageValue(
  value: string | null
): TableColumnWidthPreferences {
  if (!value) return {};
  try {
    const candidate: unknown =
      JSON.parse(value)?.state?.tableColumnWidthPreferences;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate))
      return {};
    return Object.fromEntries(
      Object.entries(candidate).map(([key, widths]) => [
        key,
        normalizeTableColumnWidths(widths),
      ])
    );
  } catch {
    return {};
  }
}

export function getTableColumnLegacyScopeClaimsFromPersistedStorageValue(
  value: string | null
): Record<string, string> {
  if (!value) return {};
  try {
    const candidate: unknown =
      JSON.parse(value)?.state?.tableColumnLegacyScopeClaims;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate))
      return {};
    return Object.fromEntries(
      Object.entries(candidate).filter(
        ([, scope]) =>
          typeof scope === 'string' && scope.length > 0 && scope.length <= 512
      )
    ) as Record<string, string>;
  } catch {
    return {};
  }
}

export function areTableColumnWidthPreferencesEqual(
  left: TableColumnWidthPreferences,
  right: TableColumnWidthPreferences
): boolean {
  const keys = Object.keys(left ?? {});
  return (
    keys.length === Object.keys(right ?? {}).length &&
    keys.every((key) => {
      const widths = left[key] ?? {};
      const other = right[key] ?? {};
      return (
        Object.keys(widths).length === Object.keys(other).length &&
        Object.entries(widths).every(
          ([column, width]) => other[column] === width
        )
      );
    })
  );
}

/**
 * Blank means clear, not zero/NaN. The actual column metadata provides the floor;
 * do not invent a global minimum that makes labels/resize behaviour disagree.
 */
export function parseTableColumnWidthInput(
  value: string,
  minimum: number,
  maximum = TABLE_COLUMN_WIDTH_MAX_PX
): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.max(minimum, Math.min(maximum, parsed));
}
